/* eslint-disable no-param-reassign */
import React from 'react';
import PropTypes from 'prop-types';
import { compose, noop } from 'underscore';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Dialog, DialogType, DialogFooter } from '@bingads-webui-react/fabric-control';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { withLock, priorityLevel, keyVault } from '@bingads-webui-react/popup-manager';
import { withUserGovernance } from '@bingads-webui-campaign-react/popup-components';
import classNames from 'classnames';
import { getMobileAppInstallPromptStyles } from './styles';
import { instrumentationSchema } from './instrumentation-schema';

const PlayStoreUrl = 'https://aka.ms/download-msads-mobile-android-prompt';
const AppStoreUrl = 'https://aka.ms/download-msads-mobile-ios-prompt';
const ESCPlayStoreUrl = 'https://aka.ms/download-msads-mobile-android-esc-prompt';
const ESCAppStoreUrl = 'https://aka.ms/download-msads-mobile-ios-esc-prompt';
const SupportedLocales = ['de', 'en', 'es', 'fr', 'it', 'pt', 'zh', 'zh-hans', 'ja'];
const DefaultBadgeLocale = 'en';

class MobileAppInstallPromptRaw extends React.PureComponent {
  constructor(props) {
    super(props);
    const {
      instrumentation,
      hidden,
      i18n,
    } = props;
    instrumentation.addScenario(instrumentationSchema);
    this.logger = instrumentation.mobileAppInstallPrompt.create().promptActivity.create();
    this.logger.trace(`Created instance. Hidden: ${hidden()}. Locale: ${i18n.getLocale()}`);
  }

  onPlayStoreClick = () => {
    this.logger.trace(`Open Play Store: ${this.PlayStoreUrl}`);
    // user governance callback, permanently dismiss the prompt
    this.props.onClose();
    this.openLinkInNewTab(this.PlayStoreUrl);
  };

  onAppStoreClick = () => {
    this.logger.trace(`Open App Store: ${this.appStoreUrl}`);
    // user governance callback, permanently dismiss the prompt
    this.props.onClose();
    this.openLinkInNewTab(this.appStoreUrl);
  };

  onContinue = () => {
    this.logger.trace('Continue');
    // user governance callback, set up the cooldown period
    this.props.onDismiss();
    this.props.handleClickCancelButton();
  };

  onDismiss = () => {
    this.logger.trace('Dismiss');
    // user governance callback, permanently dismiss the prompt
    this.props.onClose();
    this.props.handleClickCancelButton();
  };

  getLocaleClassCode = (i18nLocale) => {
    const splits = i18nLocale.split('-');
    let localeCode = splits[0] || DefaultBadgeLocale;
    if (splits[0].toLowerCase() === 'zh' && (i18nLocale.toLowerCase() === 'zh-cn' || i18nLocale.toLowerCase() === 'zh-hans')) {
      localeCode = 'zh-hans';
    }
    if (SupportedLocales.indexOf(localeCode.toLowerCase()) > -1) {
      return localeCode.toLowerCase();
    }
    return DefaultBadgeLocale;
  };

  openLinkInNewTab = (url) => {
    window.open(url, '_blank');
  };

  render() {
    const {
      classes,
      i18n,
      hidden,
      isMobileUnderDevlelopment,
      isMobileViewOnly,
      isESC,
    } = this.props;

    this.appStoreUrl = isESC ? ESCAppStoreUrl : AppStoreUrl;
    this.PlayStoreUrl = isESC ? ESCPlayStoreUrl : PlayStoreUrl;
    const title = isMobileUnderDevlelopment ? i18n.getString(_TL_('Sorry, the mobile experience is not optimized yet')):
      i18n.getString(_TL_('Manage your campaigns on the go using the app'));

    const mobileViewOnlyClasses = isMobileViewOnly ? classNames(classes.hideOnWeb, classes.mobileView) : '';

    const secondaryButtonText = this.props.secondaryButtonText || i18n.getString(_TL_('Or continue to Microsoft Advertising'));

    return (
      <Dialog
        hidden={hidden()}
        onDismiss={this.onDismiss}
        className={classNames(classes.newUINavigationPreferenceDialog, mobileViewOnlyClasses)}
        dialogContentProps={{
                  type: DialogType.close, // with x close button in the upper-right corner
                }}
        maxWidth={isMobileViewOnly ? 350 : 433}
        minWidth={isMobileViewOnly ? 0 : 433}
        modalProps={{
                  isBlocking: true,
                  dragOptions: undefined, // non-dragable
                }}
      >
        <div className="custom-content-wrapper">
          <div className="title">
            {title}
          </div>
          <div className={isMobileUnderDevlelopment ? classes.hide : 'custom-content-text'}>
            <div className="custom-content-subtitle">
              {`${i18n.getString(_TL_('Monitor performance, improve your account on the go, and so much more!'))}`}
            </div>
            <div className={isMobileViewOnly ? 'hide' : ''}>
              <div className="action-call">
                {`${i18n.getString(_TL_('Just scan this code:'))}`}
              </div>
              <div className="custom-content-qr-code-container">
                <div className="custom-content-qr-code" />
                <div className="custom-content-instructions">
                  <div className="custom-content-instruction-step">
                    {`${i18n.getString(_TL_('1. Open your phone\'s camera'))}`}
                  </div>
                  <div className="custom-content-instruction-step">
                    {`${i18n.getString(_TL_('2. Scan the QR code'))}`}
                  </div>
                  <div className="custom-content-instruction-step">
                    {`${i18n.getString(_TL_('3. Get the app'))}`}
                  </div>
                </div>
              </div>
            </div>
            <div className="custom-content-badges">
              <button
                className={`custom-content-ios-badge-${this.getLocaleClassCode(i18n.getLocale())}`}
                aria-label={`${i18n.getString(_TL_('Download on the App Store'))}`}
                onClick={this.onAppStoreClick}
                onKeyPress={this.onAppStoreClick}
              />
              <button
                className={`custom-content-android-badge-${this.getLocaleClassCode(i18n.getLocale())}`}
                aria-label={`${i18n.getString(_TL_('Get it on Google Play'))}`}
                onClick={this.onPlayStoreClick}
                onKeyPress={this.onPlayStoreClick}
              />
            </div>
          </div>
          <div className={isMobileUnderDevlelopment ?
            classes.mobileUnderDevelopmentSubtitle : classes.hide}
          >
            {`${i18n.getString(_TL_('For your best experience, we recommend managing your campaign on the desktop browser'))}`}
          </div>
          <DialogFooter className="panel-footer">
            <button
              className="btn-link"
              aria-label={secondaryButtonText}
              onClick={this.onContinue}
              onKeyPress={this.onContinue}
              tabIndex={0}
            >
              {secondaryButtonText}
            </button>
          </DialogFooter>
        </div>
      </Dialog>
    );
  }
}

MobileAppInstallPromptRaw.propTypes = {
  classes: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  instrumentation: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  hidden: PropTypes.func.isRequired,
  handleClickCancelButton: PropTypes.func.isRequired,
  onDismiss: PropTypes.func,
  onClose: PropTypes.func,
  secondaryButtonText: PropTypes.string,
  isMobileUnderDevlelopment: PropTypes.bool,
  isMobileViewOnly: PropTypes.bool,
  isESC: PropTypes.bool,
};

MobileAppInstallPromptRaw.defaultProps = {
  onDismiss: noop,
  onClose: noop,
  secondaryButtonText: '',
  isMobileUnderDevlelopment: false,
  isMobileViewOnly: false,
  isESC: false,
};

export const MobileAppInstallPrompt = compose(
  WrappedComponent => withLock(WrappedComponent, {
    priority: priorityLevel.mobilePrompt,
    lockKey: keyVault.mobilePrompt,
  }),
  WrappedComponent => withDefaultStyles(WrappedComponent, getMobileAppInstallPromptStyles),
  withInstrumentation,
  withI18nConsumer
)(MobileAppInstallPromptRaw);

export const MobileAppInstallPromptWithUserGovernance = withUserGovernance(MobileAppInstallPrompt);
